import { callCoreApi } from "../../services/coreApi";
import { crmApiFetch } from "../../services/crmApi";

export const TIME_RANGE_PRESETS = [
	"today",
	"this_week",
	"last_7_days",
	"last_30_days",
	"custom",
];

function toDateOnlyValue(date) {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

function startOfDay(date) {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

function endOfDay(date) {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

function parseLocalDateInput(value) {
	if (typeof value !== "string") {
		return new Date(value);
	}

	const trimmed = value.trim();
	const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
	if (!dateOnlyMatch) {
		return new Date(trimmed);
	}

	const year = Number(dateOnlyMatch[1]);
	const month = Number(dateOnlyMatch[2]);
	const day = Number(dateOnlyMatch[3]);
	return new Date(year, month - 1, day, 0, 0, 0, 0);
}

function startOfWeek(date) {
	const cursor = new Date(date);
	const day = cursor.getDay();
	const diff = day === 0 ? -6 : 1 - day;
	cursor.setDate(cursor.getDate() + diff);
	return startOfDay(cursor);
}

export function resolveTimeRange({ preset, customFrom, customTo }) {
	const now = new Date();
	let fromDate = null;
	let toDate = null;

	switch (preset) {
		case "today": {
			fromDate = startOfDay(now);
			toDate = endOfDay(now);
			break;
		}
		case "this_week": {
			fromDate = startOfWeek(now);
			toDate = endOfDay(now);
			break;
		}
		case "last_7_days": {
			const from = new Date(now);
			from.setDate(from.getDate() - 6);
			fromDate = startOfDay(from);
			toDate = endOfDay(now);
			break;
		}
		case "last_30_days": {
			const from = new Date(now);
			from.setDate(from.getDate() - 29);
			fromDate = startOfDay(from);
			toDate = endOfDay(now);
			break;
		}
		case "custom": {
			if (!customFrom || !customTo) {
				return {
					from: null,
					to: null,
					fromDateValue: "",
					toDateValue: "",
				};
			}
			fromDate = startOfDay(parseLocalDateInput(customFrom));
			toDate = endOfDay(parseLocalDateInput(customTo));
			break;
		}
		default: {
			fromDate = startOfWeek(now);
			toDate = endOfDay(now);
		}
	}

	if (Number.isNaN(fromDate?.getTime?.()) || Number.isNaN(toDate?.getTime?.()) || fromDate > toDate) {
		return {
			from: null,
			to: null,
			fromDateValue: "",
			toDateValue: "",
		};
	}

	return {
		from: fromDate.toISOString(),
		to: toDate.toISOString(),
		fromDateValue: toDateOnlyValue(fromDate),
		toDateValue: toDateOnlyValue(toDate),
	};
}

export async function listOrganizationTimeEntries({ from, to, userId = "all", limit = 1000 }) {
	const params = new URLSearchParams();
	if (from) {
		params.set("from", from);
	}
	if (to) {
		params.set("to", to);
	}
	if (userId && userId !== "all") {
		params.set("user_id", String(userId));
	}
	params.set("limit", String(limit));

	const payload = await callCoreApi(`/api/time-clock/entries?${params.toString()}`, {
		method: "GET",
	});

	return Array.isArray(payload?.entries) ? payload.entries : [];
}

export async function listTeamMembers() {
	const response = await crmApiFetch("/api/contacts/team-members", {
		method: "GET",
	});

	let payload = {};
	try {
		payload = await response.json();
	} catch {
		payload = {};
	}

	if (!response.ok) {
		throw new Error(payload?.error || "Failed to fetch team members");
	}

	return Array.isArray(payload?.team_members) ? payload.team_members : [];
}

export function createDefaultFilters() {
	const now = new Date();
	const today = toDateOnlyValue(now);
	return {
		preset: "this_week",
		customFrom: "",
		customTo: "",
		focusDate: today,
		userId: "all",
	};
}
